import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/admin.api';
import { ShieldCheck, ShieldOff, AlertTriangle, Building2, Trash2 } from 'lucide-react';

type StatusFilter = 'unverified' | 'flagged' | 'all';

export function CompaniesManagement() {
  const [filter, setFilter] = useState<StatusFilter>('unverified');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [purgeTarget, setPurgeTarget] = useState<{ id: string; name: string } | null>(null);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: companies = [], isFetching, error } = useQuery({
    queryKey: ['admin-companies', filter],
    queryFn: () => adminApi.listCompanies(filter),
  });

  const verifyMutation = useMutation({
    mutationFn: (id: string) => adminApi.verifyCompany(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-companies'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminApi.rejectCompany(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      setRejectId(null);
      setRejectReason('');
    },
  });

  const purgeMutation = useMutation({
    mutationFn: (id: string) => adminApi.purgeCompany(id),
    onSuccess: (result) => {
      setNotice(
        `"${result.name}" permanently deleted` +
          (result.assetsDeleted > 0 ? ` (${result.assetsDeleted} uploaded file(s) removed).` : '.')
      );
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      setPurgeTarget(null);
    },
    onError: (err: any) => setNotice(err?.message ?? 'Could not delete the company.'),
  });

  const filterLabels: Record<StatusFilter, string> = {
    unverified: 'Unverified',
    flagged: 'Flagged',
    all: 'All',
  };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-4">Companies</h1>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        {(['unverified', 'flagged', 'all'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-gray-900 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {filterLabels[f]}
          </button>
        ))}
      </div>

      {isFetching && (
        <p className="text-gray-500 text-sm mb-4">Loading...</p>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          Failed to load companies.
        </div>
      )}

      {notice && (
        <div className="mb-4 flex items-start justify-between gap-3 bg-gray-50 border border-gray-200 text-gray-800 px-4 py-3 rounded-lg text-sm">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-gray-400 hover:text-gray-600">
            Dismiss
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border rounded overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3">Company</th>
              <th className="p-3">Industry</th>
              <th className="p-3">Website</th>
              <th className="p-3">Jobs</th>
              <th className="p-3">Status</th>
              <th className="p-3">Created</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c: any) => (
              <tr key={c.id} className="border-t hover:bg-gray-50 align-middle">
                <td className="p-3 font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                  {c.name}
                  {c.flaggedForReview && (
                    <span className="ml-1 inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                      <AlertTriangle className="w-3 h-3" /> Flagged
                    </span>
                  )}
                </td>
                <td className="p-3 text-sm text-gray-600">{c.industry || '—'}</td>
                <td className="p-3 text-sm">
                  {c.website ? (
                    <a
                      href={c.website}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-500 hover:underline truncate max-w-[140px] block"
                    >
                      {c.website.replace(/^https?:\/\//, '')}
                    </a>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="p-3 text-sm text-center">{c._count?.jobPostings ?? 0}</td>
                <td className="p-3">
                  {c.emailVerified ? (
                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                      <ShieldCheck className="w-3 h-3" /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                      <AlertTriangle className="w-3 h-3" /> Unverified
                    </span>
                  )}
                </td>
                <td className="p-3 text-sm text-gray-500">
                  {new Date(c.createdAt).toLocaleDateString()}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {!c.emailVerified && (
                      <button
                        onClick={() => verifyMutation.mutate(c.id)}
                        disabled={verifyMutation.isPending}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded transition-colors"
                      >
                        <ShieldCheck className="w-3 h-3" /> Verify
                      </button>
                    )}
                    <button
                      onClick={() => { setRejectId(c.id); setRejectReason(''); }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-medium rounded transition-colors"
                    >
                      <ShieldOff className="w-3 h-3" /> Reject
                    </button>
                    {/* Permanent delete is offered only for companies never
                        attached to a job. With jobs, the FK is SET NULL, so
                        deleting would strip the employer off those postings. */}
                    {(c._count?.jobPostings ?? 0) === 0 ? (
                      <button
                        onClick={() => setPurgeTarget({ id: c.id, name: c.name })}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    ) : (
                      <span
                        className="text-xs text-gray-400"
                        title={`Has ${c._count?.jobPostings} job posting(s) — deleting would strip the employer from them. Use Reject to deactivate instead.`}
                      >
                        Has jobs
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!isFetching && companies.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No companies found for this filter.
          </div>
        )}
      </div>

      {/* Reject confirmation modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-slate-950/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Reject company</h2>
            <p className="text-sm text-gray-600 mb-4">
              This will deactivate the company and all its jobs will remain but the company will be
              marked inactive. Provide an optional reason.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRejectId(null)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => rejectMutation.mutate({ id: rejectId, reason: rejectReason })}
                disabled={rejectMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
              >
                {rejectMutation.isPending ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent delete confirmation */}
      {purgeTarget && (
        <div className="fixed inset-0 bg-slate-950/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-base font-semibold text-gray-900 mb-2">
              Permanently delete "{purgeTarget.name}"?
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              This cannot be undone. The company record and any uploaded logo, photos or videos
              are removed for good. It is only offered because this company has never been
              attached to a job posting — so no job history is affected.
            </p>
            <label className="block text-sm mb-4">
              <span className="mb-1 block font-medium text-gray-700">
                Type <code className="font-mono">DELETE</code> to confirm
              </span>
              <input
                type="text"
                value={purgeConfirmText}
                onChange={(e) => setPurgeConfirmText(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
              />
            </label>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setPurgeTarget(null);
                  setPurgeConfirmText('');
                }}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  purgeMutation.mutate(purgeTarget.id);
                  setPurgeConfirmText('');
                }}
                disabled={purgeConfirmText.trim().toUpperCase() !== 'DELETE' || purgeMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purgeMutation.isPending ? 'Deleting...' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
